import {issuePath} from '@github-ui/paths'
import {CommandButton, type CommandId, GlobalCommands} from '@github-ui/ui-commands'
import {InfoIcon} from '@primer/octicons-react'
import {Button, Checkbox, FormControl, Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {BUTTON_LABELS} from './constants/buttons'
import {LABELS} from './constants/labels'
import {TEST_IDS} from './constants/test-ids'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import styles from './CreateIssueFooter.module.css'
import type {DuplicateDetectionState} from './hooks/use-duplicate-detection'
import type {IssueToPreheat, PotentialDuplicateIssue} from './PotentialDuplicates'
import {PotentialDuplicates} from './PotentialDuplicates'

type CreateIssueFooterProps = {
  className?: string
  hideCreateMore?: boolean
  onClose?: () => void
  /** Called when user clicks on a duplicate issue to support SidePanel. If not provided, issues open in new tab. */
  onDuplicateIssueSidePanelClick?: (issue: PotentialDuplicateIssue) => void
  /** Used to preheat IssueViewer queries when SidePanel is enabled. */
  onDuplicateIssuesToPreheat?: (issues: IssueToPreheat[]) => void
  /** Duplicate detection state from useDuplicateDetection hook */
  duplicateDetection?: DuplicateDetectionState
}

export const CreateIssueFooter = ({
  className,
  hideCreateMore = false,
  onClose,
  onDuplicateIssueSidePanelClick,
  onDuplicateIssuesToPreheat,
  duplicateDetection,
}: CreateIssueFooterProps) => {
  const {
    createMore,
    setCreateMore,
    createMoreCreatedPath,
    onCreateAction,
    isSubmitting,
    isFileUploading,
    isSubIssue,
    optionConfig: {insidePortal},
  } = useIssueCreateConfigContext()

  const {repository} = useIssueCreateDataContext()

  const isDuplicateDetectionEnabled = duplicateDetection && duplicateDetection.isDuplicateDetectionEnabled
  const showDuplicateDetection =
    isDuplicateDetectionEnabled && duplicateDetection.items.length > 0 && !duplicateDetection.isError

  const handleCheckMoreChange = () => {
    setCreateMore(!createMore)
  }

  const handleCreateMore = () => {
    setCreateMore(true)
    if (!shouldSubmit()) {
      return
    }
    onCreateAction?.current?.onCreate(isSubmitting, true)
  }

  const [createWaitingOnUpload, setCreateWaitingOnUpload] = useState<boolean>(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const checkboxRef = useRef<HTMLInputElement>(null)
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  const shouldSubmit = () => {
    if (isSubmitting) {
      return false
    }

    // do not submit if files are still uploading
    if (isFileUploading) {
      setCreateWaitingOnUpload(true)
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    if (!shouldSubmit()) {
      return
    }

    onCreateAction?.current?.onCreate(isSubmitting, createMore)
  }

  // runs when isFileUploading changes
  useEffect(() => {
    // if the file is not uploading anymore and the submit button was clicked and is not currently submitting, click is emitted
    if (buttonRef.current && !isFileUploading && !isSubmitting && createWaitingOnUpload) {
      setCreateWaitingOnUpload(false)
      buttonRef.current.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true}))
    }
  }, [isFileUploading, isSubmitting, createWaitingOnUpload])

  const commands: Partial<Record<CommandId, () => void>> = {
    'github:submit-form': handleSubmit,
  }

  if (!hideCreateMore) {
    commands['issue-create:submit-and-create-more'] = handleCreateMore
  }

  const handleDismissDuplicates = useCallback(() => {
    duplicateDetection?.dismissCurrentResults()
    if (!hideCreateMore) {
      checkboxRef.current?.focus()
    } else {
      const fallbackFocusTarget = cancelButtonRef.current ?? buttonRef.current
      fallbackFocusTarget?.focus()
    }
  }, [duplicateDetection, hideCreateMore])

  return (
    <div
      className={clsx(
        styles.container,
        className,
        styles.CreateIssueFooterContainer,
        showDuplicateDetection && styles.duplicateDetectionShownWrapper,
      )}
    >
      <GlobalCommands commands={commands} />
      {createWaitingOnUpload && (
        <div className={styles.fileUploadWarningSection}>
          <Octicon icon={InfoIcon} size={16} />
          <span className={styles.warningMessageText}>{LABELS.fileUploadWarning}</span>
        </div>
      )}
      {showDuplicateDetection && repository && duplicateDetection && (
        <PotentialDuplicates
          items={duplicateDetection.items}
          isError={duplicateDetection.isError}
          repoName={repository.name}
          repoOwner={repository.owner.login}
          onIssueClick={onDuplicateIssueSidePanelClick}
          onIssueUpdate={duplicateDetection.updateIssue}
          onIssueClickTracking={duplicateDetection.trackIssueClicked}
          onIssuesToPreheat={onDuplicateIssuesToPreheat}
          sessionResetKey={duplicateDetection.sessionResetKey}
          onDismiss={handleDismissDuplicates}
        />
      )}
      <div
        className={clsx(
          styles.mainContentWrapper,
          isDuplicateDetectionEnabled && styles.duplicateDetectionEnabledActionsGroup,
        )}
      >
        {!hideCreateMore && (
          <div className={clsx(styles.checkboxAndLinksSection, !insidePortal && styles.checkboxMarginLeft)}>
            <FormControl className={styles.FormControl}>
              <Checkbox
                ref={checkboxRef}
                data-testid={TEST_IDS.createMoreIssuesCheckbox}
                checked={createMore}
                onChange={handleCheckMoreChange}
                className={styles.Checkbox}
              />
              <FormControl.Label>
                {isSubIssue ? BUTTON_LABELS.createMoreSubIssues : BUTTON_LABELS.createMore}
              </FormControl.Label>
            </FormControl>
            <div
              data-testid={TEST_IDS.issueCreatedAnnouncement}
              className="sr-only"
              aria-live="polite"
              aria-atomic="true"
              role="status"
            >
              {createMoreCreatedPath.number ? LABELS.lastIssueCreated(createMoreCreatedPath.number) : null}
            </div>
            {createMoreCreatedPath.number ? (
              <>
                <span className={styles.separatorDot}>·</span>
                <Link
                  href={issuePath({
                    owner: createMoreCreatedPath.owner,
                    repo: createMoreCreatedPath.repo,
                    issueNumber: createMoreCreatedPath.number,
                  })}
                  data-testid={TEST_IDS.issueCreatedLink}
                >
                  {LABELS.lastIssueCreated(createMoreCreatedPath.number)}
                </Link>
              </>
            ) : null}
          </div>
        )}
        <div className={styles.actionButtonsContainer}>
          {onClose && (
            <Button ref={cancelButtonRef} onClick={() => onClose()}>
              {BUTTON_LABELS.cancel}
            </Button>
          )}
          <CommandButton
            commandId="github:submit-form"
            variant="primary"
            inactive={isSubmitting || isFileUploading}
            data-testid={TEST_IDS.createIssueButton}
            ref={buttonRef}
            showKeybindingHint
          >
            {BUTTON_LABELS.create}
          </CommandButton>
        </div>
      </div>
    </div>
  )
}
